"use strict";
Object.defineProperty(exports, "__esModule", {
    value: true
});
exports.useBasketContext = exports.basketContext = exports.BasketContextProvider = void 0;
var _basketItem = require("@ori/basket-item");
var _checkoutUtils = require("@ori/checkout-utils");
var _environment = require("@ori/environment");
var _shoppingContext = require("@ori/shopping-context");
var _staticApiClientReact = require("@ori/static-api-client-react");
var _react = require("react");
var _constants = require("../constants");
var _hooks = require("../hooks");
var _logger = require("../logger");
var _jsxRuntime = require("react/jsx-runtime");
const basketContext = exports.basketContext = /*#__PURE__*/ (0, _react.createContext)(_constants.defaultBasketContext);
const BasketContextProvider = (param)=>{
    let { shouldMutateCombineSection, setShouldMutateCombineSection, children } = param;
    const { shoppingContext } = (0, _shoppingContext.useShoppingContext)();
    const { tenant } = (0, _environment.useEnvironment)();
    const { data: { isEnabled: isPersonalSubscriptionsEnabled } = {
        isEnabled: undefined
    } } = (0, _staticApiClientReact.useGetTenantFeatureToggle)(tenant, _constants.PERSONAL_SUBSCRIPTIONS, {
        swr: (0, _checkoutUtils.getRetryErrorSwrOptions)()
    });
    const { translations } = (0, _hooks.useTranslations)();
    const basketItemTranslations = (0, _react.useMemo)(()=>({
            ..._basketItem.basketItemDefaultTranslations,
            ...translations
        }), [
        translations
    ]);
    const basketItemTranslationsNamespace = _constants.translationsNamespace;
    const { atpClusterId, isAnonymous = false, isConsultant = false, shoppingMessages = [], showBp: showBpGlobally = false, unitDataCollection = [], shoppingBag } = shoppingContext !== null && shoppingContext !== void 0 ? shoppingContext : {};
    // state that holds information about what line number triggered loading action
    // set to -1 to reset
    const [loadingItem, setLoadingItem] = (0, _react.useState)(-1);
    (0, _logger.logDebug)('shoppingContext: ', shoppingContext);
    (0, _logger.logDebug)('loadingItem: ', loadingItem);
    var _shoppingContext_shoppingBag;
    const { addedItems = [], backorderItems = [], basketOffers = [], isDiscounted = false, offers: rawOffers = [], reservedItems = [], shoppingBagItems = [], subscriptions = [], vipLoyalty } = (_shoppingContext_shoppingBag = shoppingContext === null || shoppingContext === void 0 ? void 0 : shoppingContext.shoppingBag) !== null && _shoppingContext_shoppingBag !== void 0 ? _shoppingContext_shoppingBag : {};
    const offers = (0, _react.useMemo)(()=>rawOffers.filter((x)=>x.offerType !== _constants.SHIPPING_OFFER), [
        rawOffers
    ]);
    const distinctOffers = (0, _react.useMemo)(()=>offers.filter((offer, index)=>index === offers.findIndex((innerOffer)=>innerOffer.choiceId === offer.choiceId)), [
        offers
    ]);
    const shoppingBagItemsValid = (0, _react.useMemo)(()=>shoppingBagItems.filter((x)=>x.itemType !== 'U'), [
        shoppingBagItems
    ]);
    const splitoutItems = (0, _react.useMemo)(()=>shoppingBagItems.filter((x)=>x.itemType === 'X'), [
        shoppingBagItems
    ]);
    // INFO: splitoutItemsMap holds information about splitout items and their parent line numbers
    //       this is used to handle correctly loading of main and splitout item and to have true information in main item quantity picker
    const splitoutItemsMap = (0, _react.useMemo)(()=>{
        if (splitoutItems.length === 0) {
            return undefined;
        }
        return splitoutItems.map((item)=>{
            var _shoppingBagItemsValid_find;
            const parentLineNumber = (_shoppingBagItemsValid_find = shoppingBagItemsValid.find((x)=>x.itemCode === item.itemCode && x.itemType !== 'X')) === null || _shoppingBagItemsValid_find === void 0 ? void 0 : _shoppingBagItemsValid_find.lineNumber;
            const splitoutLineNumber = item.lineNumber;
            const splitoutRequestedQuantity = item.requestedQuantity;
            return {
                itemCode: item.itemCode,
                parentLineNumber,
                splitoutLineNumber,
                splitoutRequestedQuantity
            };
        });
    }, [
        shoppingBagItemsValid,
        splitoutItems
    ]);
    // empty basket
    const isEmptyBasket = shoppingBagItemsValid.length === 0 && backorderItems.length === 0 && offers.length === 0;
    // Added sections (free, paid and reserved)
    const added = (0, _react.useMemo)(()=>{
        const free = [];
        const paid = [];
        addedItems.forEach((item)=>{
            if (item.isFree) {
                free.push(item);
            } else {
                paid.push(item);
            }
        });
        paid.push(...reservedItems);
        return {
            free,
            paid
        };
    }, [
        addedItems,
        reservedItems
    ]);
    const addedFree = (0, _react.useMemo)(()=>added.free, [
        added.free
    ]);
    const addedPaid = (0, _react.useMemo)(()=>[
            ...added.paid,
            ...reservedItems
        ], [
        added.paid,
        reservedItems
    ]);
    // shipping offers
    const shippingOffer = (0, _react.useMemo)(()=>rawOffers.find((x)=>x.offerType === _constants.SHIPPING_OFFER), [
        rawOffers
    ]);
    // end consumer orders
    const consumerOrders = (0, _react.useMemo)(()=>unitDataCollection.find((x)=>x.unitName === _constants.PENDING_EC_ORDERS), [
        unitDataCollection
    ]);
    // stuff for banner area
    const vouchers = (0, _react.useMemo)(()=>unitDataCollection.filter((x)=>x.unitName === _constants.VOUCHERS), [
        unitDataCollection
    ]);
    const message = (0, _react.useMemo)(()=>shoppingMessages.find((x)=>x.hintType === _constants.ADVICE_HINT_TYPE && x.hintCategory === _constants.WELCOME_PROGRAM_CATEGORY), [
        shoppingMessages
    ]);
    const showBannerSection = Boolean(vipLoyalty) || basketOffers.length > 0 || Boolean(message) || vouchers.length > 0;
    const value = (0, _react.useMemo)(()=>({
            addedFree,
            addedPaid,
            atpClusterId,
            backorderItems,
            basketItemTranslations,
            basketItemTranslationsNamespace,
            basketOffers,
            consumerOrders,
            distinctOffers,
            isAnonymous,
            isConsultant,
            isDiscounted,
            isEmptyBasket,
            isPersonalSubscriptionsEnabled,
            loadingItem,
            message,
            shoppingBag,
            shoppingBagItems,
            shoppingBagItemsValid,
            shouldMutateCombineSection,
            shippingOffer,
            shoppingMessages,
            showBannerSection,
            showBpGlobally,
            splitoutItemsMap,
            subscriptions,
            vipLoyalty,
            vouchers,
            setLoadingItem,
            setShouldMutateCombineSection
        }), [
        addedFree,
        addedPaid,
        atpClusterId,
        backorderItems,
        basketItemTranslations,
        basketItemTranslationsNamespace,
        basketOffers,
        consumerOrders,
        distinctOffers,
        isAnonymous,
        isConsultant,
        isDiscounted,
        isEmptyBasket,
        isPersonalSubscriptionsEnabled,
        loadingItem,
        message,
        shoppingBag,
        shoppingBagItems,
        shoppingBagItemsValid,
        shouldMutateCombineSection,
        shippingOffer,
        shoppingMessages,
        showBannerSection,
        showBpGlobally,
        splitoutItemsMap,
        subscriptions,
        vipLoyalty,
        vouchers,
        setLoadingItem,
        setShouldMutateCombineSection
    ]);
    (0, _logger.logDebug)('basketContext: ', value);
    return /*#__PURE__*/ (0, _jsxRuntime.jsx)(basketContext.Provider, {
        value: value,
        children: children
    });
};
exports.BasketContextProvider = BasketContextProvider;
const useBasketContext = ()=>(0, _react.useContext)(basketContext);
exports.useBasketContext = useBasketContext;
